/**
 * Mock Data for Demo Application
 *
 * This file contains all mock data for demonstrating the ManuscriptMatch platform.
 * Includes fake users, manuscripts, applications, queries, and feedback.
 */

// User roles
export type UserRole = 'WRITER' | 'READER' | 'AGENT' | 'AGENCY';

// Mock Users
export interface MockUser {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  password: string; // For demo purposes only
  profile?: WriterProfile | ReaderProfile | AgentProfile | AgencyProfile;
  verified: boolean;
  createdAt: string;
}

export interface WriterProfile {
  bio: string;
  genres: string[];
  website?: string;
  twitter?: string;
  instagram?: string;
}

export interface ReaderProfile {
  bio: string;
  favoriteGenres: string[];
  readingSpeed: string;
  feedback: string;
  strengths: string[];
  availability: string;
}

export interface AgentProfile {
  agencyName: string;
  bio: string;
  mswl: string[];
  website?: string;
  queryGuidelines?: string;
  responseTime?: string;
  acceptingQueries: boolean;
}

export interface AgencyProfile {
  name: string;
  bio: string;
  website?: string;
  founded?: string;
  location?: string;
}

// Mock Manuscripts
export interface MockManuscript {
  id: string;
  writerId: string;
  title: string;
  genre: string;
  wordCount: number;
  blurb: string;
  contentWarnings?: string;
  excerpt?: string;
  status: 'DRAFTING' | 'SEEKING_BETA_READERS' | 'QUERYING';
  targetAudience?: string;
  completionStatus?: string;
  betaDeadline?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock Beta Applications
export interface MockBetaApplication {
  id: string;
  manuscriptId: string;
  readerId: string;
  status: 'PENDING' | 'ACCEPTED' | 'REJECTED' | 'COMPLETED' | 'WITHDRAWN';
  message: string;
  availability: string;
  readingSpeed: 'SLOW' | 'MODERATE' | 'FAST';
  experienceLevel: 'BEGINNER' | 'INTERMEDIATE' | 'EXPERIENCED';
  strengths: string[];
  createdAt: string;
  updatedAt: string;
}

// Mock Queries
export interface MockQuery {
  id: string;
  writerId: string;
  agentId: string;
  manuscriptId?: string;
  manuscriptTitle: string;
  genre: string;
  wordCount: number;
  queryLetter: string;
  excerpt?: string;
  status: 'SUBMITTED' | 'UNDER_REVIEW' | 'REQUESTED' | 'REJECTED' | 'OFFERED';
  agentResponse?: string;
  createdAt: string;
  updatedAt: string;
}

// Mock Feedback
export interface MockFeedback {
  id: string;
  manuscriptId: string;
  readerId: string;
  overallImpression: string;
  strengths: string;
  weaknesses: string;
  suggestions: string;
  rating: number;
  createdAt: string;
}

// MOCK DATA

export const mockUsers: MockUser[] = [
  // Writers
  {
    id: 'writer-1',
    email: 'sarah.writer@demo.com',
    name: 'Sarah Thompson',
    role: 'WRITER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-15T10:00:00Z',
    profile: {
      bio: 'Fantasy author working on my debut novel. Love epic worldbuilding and complex magic systems.',
      genres: ['Fantasy', 'Young Adult'],
      website: 'https://sarahwrites.com',
      twitter: '@sarahwrites',
    } as WriterProfile,
  },
  {
    id: 'writer-2',
    email: 'james.novelist@demo.com',
    name: 'James Chen',
    role: 'WRITER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-02-01T10:00:00Z',
    profile: {
      bio: 'Mystery/thriller writer. Former detective turned author. I write what I know.',
      genres: ['Mystery', 'Thriller'],
      website: 'https://jameschen.com',
    } as WriterProfile,
  },
  {
    id: 'writer-3',
    email: 'emma.author@demo.com',
    name: 'Emma Rodriguez',
    role: 'WRITER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-20T10:00:00Z',
    profile: {
      bio: 'Contemporary romance author with a passion for diverse characters and heartfelt stories.',
      genres: ['Romance', 'Contemporary'],
      instagram: '@emmawritesromance',
    } as WriterProfile,
  },

  // Readers
  {
    id: 'reader-1',
    email: 'alex.reader@demo.com',
    name: 'Alex Martinez',
    role: 'READER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-10T10:00:00Z',
    profile: {
      bio: 'Avid fantasy reader and aspiring editor. I love helping authors polish their work.',
      favoriteGenres: ['Fantasy', 'Science Fiction', 'Young Adult'],
      readingSpeed: '50-75 pages per day',
      feedback: 'Detailed developmental feedback with focus on plot and character arcs',
      strengths: ['Plot structure', 'Character development', 'World-building'],
      availability: '2-3 manuscripts per quarter',
    } as ReaderProfile,
  },
  {
    id: 'reader-2',
    email: 'maya.booklover@demo.com',
    name: 'Maya Patel',
    role: 'READER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-02-05T10:00:00Z',
    profile: {
      bio: 'English teacher and beta reader. Specializing in grammar, pacing, and dialogue.',
      favoriteGenres: ['Literary Fiction', 'Historical Fiction', 'Mystery'],
      readingSpeed: '30-50 pages per day',
      feedback: 'Line-by-line feedback on prose and grammar',
      strengths: ['Grammar and syntax', 'Dialogue', 'Pacing'],
      availability: '1-2 manuscripts per quarter',
    } as ReaderProfile,
  },
  {
    id: 'reader-3',
    email: 'chris.critique@demo.com',
    name: 'Chris Johnson',
    role: 'READER',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-25T10:00:00Z',
    profile: {
      bio: 'Romance and YA enthusiast. I read 100+ books a year and love giving feedback.',
      favoriteGenres: ['Romance', 'Young Adult', 'Contemporary'],
      readingSpeed: '75-100 pages per day',
      feedback: 'Focus on emotional resonance and character relationships',
      strengths: ['Character relationships', 'Emotional depth', 'Romance arcs'],
      availability: '3-4 manuscripts per quarter',
    } as ReaderProfile,
  },

  // Agents
  {
    id: 'agent-1',
    email: 'jennifer.agent@demo.com',
    name: 'Jennifer Walsh',
    role: 'AGENT',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-05T10:00:00Z',
    profile: {
      agencyName: 'Sterling Literary Agency',
      bio: 'Seeking fresh voices in fantasy and science fiction. I love complex worldbuilding and diverse casts.',
      mswl: [
        'Adult Fantasy with unique magic systems',
        'Science Fiction with social commentary',
        'Young Adult Fantasy',
      ],
      website: 'https://sterlingliterary.com',
      queryGuidelines: 'Query letter + first 10 pages',
      responseTime: '4-6 weeks',
      acceptingQueries: true,
    } as AgentProfile,
  },
  {
    id: 'agent-2',
    email: 'michael.rep@demo.com',
    name: 'Michael Brooks',
    role: 'AGENT',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-08T10:00:00Z',
    profile: {
      agencyName: 'Brooks & Associates',
      bio: 'Commercial fiction specialist. Looking for page-turners in mystery, thriller, and suspense.',
      mswl: [
        'Mystery with strong voice',
        'Psychological thrillers',
        'Crime fiction',
        'Suspense novels',
      ],
      website: 'https://brooksassociates.com',
      queryGuidelines: 'Query letter + synopsis + first chapter',
      responseTime: '6-8 weeks',
      acceptingQueries: true,
    } as AgentProfile,
  },
  {
    id: 'agent-3',
    email: 'lisa.literary@demo.com',
    name: 'Lisa Anderson',
    role: 'AGENT',
    password: 'demo123',
    verified: true,
    createdAt: '2024-01-12T10:00:00Z',
    profile: {
      agencyName: 'Anderson Literary Group',
      bio: 'Romance and women\'s fiction agent. I\'m passionate about character-driven stories with heart.',
      mswl: [
        'Contemporary romance',
        'Romantic comedy',
        'Women\'s fiction',
        'Book club fiction',
      ],
      website: 'https://andersonlit.com',
      queryGuidelines: 'Query letter only (no attachments)',
      responseTime: '3-4 weeks',
      acceptingQueries: true,
    } as AgentProfile,
  },
];

export const mockManuscripts: MockManuscript[] = [
  {
    id: 'ms-1',
    writerId: 'writer-1',
    title: 'The Crystal Throne',
    genre: 'Fantasy',
    wordCount: 95000,
    blurb: 'In a world where magic is tied to ancient crystals, a young thief must steal the most powerful crystal of all to save her dying sister. But the crystal has other plans.',
    contentWarnings: 'Violence, loss of a parent',
    excerpt: 'Chapter 1\n\nThe crystal market sparkled in the afternoon sun, each vendor\'s wares catching the light and throwing rainbow patterns across the cobblestones...',
    status: 'SEEKING_BETA_READERS',
    targetAudience: 'Young Adult',
    completionStatus: 'First draft complete',
    betaDeadline: '2024-05-15T10:00:00Z',
    createdAt: '2024-03-01T10:00:00Z',
    updatedAt: '2024-03-15T10:00:00Z',
  },
  {
    id: 'ms-2',
    writerId: 'writer-2',
    title: 'The Last Witness',
    genre: 'Mystery',
    wordCount: 78000,
    blurb: 'Detective Sarah Kane thought she\'d seen it all. Then a witness to a decades-old cold case surfaces, and everything Sarah thought she knew about her own past comes into question.',
    contentWarnings: 'Violence, mentions of child abuse',
    excerpt: 'Prologue\n\nThe woman in the photo looked familiar, but Sarah couldn\'t place her. She stared at the grainy image, twenty years old if it was a day...',
    status: 'QUERYING',
    targetAudience: 'Adult',
    completionStatus: 'Polished and ready',
    createdAt: '2024-02-15T10:00:00Z',
    updatedAt: '2024-04-01T10:00:00Z',
  },
  {
    id: 'ms-3',
    writerId: 'writer-3',
    title: 'Love in the Time of Coffee',
    genre: 'Romance',
    wordCount: 65000,
    blurb: 'When Mia inherits her grandmother\'s coffee shop, she never expected to fall for the uptight businessman trying to buy the building. A sweet, steamy romance about second chances and finding home.',
    contentWarnings: 'None',
    excerpt: 'Chapter 1\n\nMia had always loved the smell of coffee. Not the weak, watery stuff from chain stores, but the rich, bold aroma of freshly ground beans...',
    status: 'SEEKING_BETA_READERS',
    targetAudience: 'Adult',
    completionStatus: 'First draft complete',
    betaDeadline: '2024-05-01T10:00:00Z',
    createdAt: '2024-03-10T10:00:00Z',
    updatedAt: '2024-03-20T10:00:00Z',
  },
  {
    id: 'ms-4',
    writerId: 'writer-1',
    title: 'Shadow Kingdom',
    genre: 'Fantasy',
    wordCount: 120000,
    blurb: 'A dark fantasy epic where a princess must master forbidden shadow magic to reclaim her throne from the uncle who murdered her family.',
    contentWarnings: 'Violence, death, dark themes',
    status: 'DRAFTING',
    targetAudience: 'Adult',
    completionStatus: 'Work in progress - 50% complete',
    createdAt: '2024-04-05T10:00:00Z',
    updatedAt: '2024-10-28T10:00:00Z',
  },
  {
    id: 'ms-5',
    writerId: 'writer-2',
    title: 'The Memory Thieves',
    genre: 'Science Fiction',
    wordCount: 88000,
    blurb: 'In a future where memories can be extracted and sold, a black-market memory dealer discovers a conspiracy that threatens to erase history itself. A fast-paced sci-fi thriller about identity, truth, and the price of forgetting.',
    contentWarnings: 'Violence, psychological manipulation',
    excerpt: 'Chapter 1\n\nThe memory clinic was tucked between a noodle shop and a pawn broker, its neon sign flickering in the rain. Maya had been here a dozen times, but tonight felt different...',
    status: 'SEEKING_BETA_READERS',
    targetAudience: 'Adult',
    completionStatus: 'First draft complete',
    betaDeadline: '2024-05-20T10:00:00Z',
    createdAt: '2024-03-25T10:00:00Z',
    updatedAt: '2024-04-10T10:00:00Z',
  },
];

export const mockBetaApplications: MockBetaApplication[] = [
  {
    id: 'app-1',
    manuscriptId: 'ms-1',
    readerId: 'reader-1',
    status: 'ACCEPTED',
    message: 'I love fantasy with unique magic systems! Your premise sounds fascinating and I\'d be honored to beta read. I can provide detailed feedback on plot, character development, and world-building.',
    availability: 'Can complete in 3-4 weeks',
    readingSpeed: 'MODERATE',
    experienceLevel: 'EXPERIENCED',
    strengths: ['Plot structure', 'Character development', 'World-building'],
    createdAt: '2024-03-16T10:00:00Z',
    updatedAt: '2024-03-17T10:00:00Z',
  },
  {
    id: 'app-2',
    manuscriptId: 'ms-1',
    readerId: 'reader-3',
    status: 'ACCEPTED',
    message: 'YA fantasy is my jam! I read about 5 books a month in this genre and would love to help. Strong with character relationships and emotional beats.',
    availability: 'Can complete in 2-3 weeks',
    readingSpeed: 'FAST',
    experienceLevel: 'EXPERIENCED',
    strengths: ['Character relationships', 'Emotional depth', 'Pacing'],
    createdAt: '2024-03-16T14:00:00Z',
    updatedAt: '2024-03-17T10:00:00Z',
  },
  {
    id: 'app-3',
    manuscriptId: 'ms-1',
    readerId: 'reader-2',
    status: 'PENDING',
    message: 'While fantasy isn\'t my primary genre, I\'d be happy to offer feedback on prose, dialogue, and pacing. My background as an English teacher helps me catch grammar and style issues.',
    availability: 'Can complete in 4-5 weeks',
    readingSpeed: 'SLOW',
    experienceLevel: 'INTERMEDIATE',
    strengths: ['Grammar and syntax', 'Dialogue', 'Pacing'],
    createdAt: '2024-03-18T10:00:00Z',
    updatedAt: '2024-03-18T10:00:00Z',
  },
  {
    id: 'app-4',
    manuscriptId: 'ms-3',
    readerId: 'reader-3',
    status: 'ACCEPTED',
    message: 'Contemporary romance is my favorite genre! I\'ve beta read 20+ romance novels. I focus on emotional authenticity, chemistry, and satisfying character arcs.',
    availability: 'Can complete in 2 weeks',
    readingSpeed: 'FAST',
    experienceLevel: 'EXPERIENCED',
    strengths: ['Character relationships', 'Emotional depth', 'Romance arcs'],
    createdAt: '2024-03-21T10:00:00Z',
    updatedAt: '2024-03-22T10:00:00Z',
  },
  {
    id: 'app-5',
    manuscriptId: 'ms-3',
    readerId: 'reader-1',
    status: 'PENDING',
    message: 'This sounds like a sweet story! While romance isn\'t my usual genre, I enjoy contemporary fiction and would be happy to provide feedback on plot and character development.',
    availability: 'Can complete in 3 weeks',
    readingSpeed: 'MODERATE',
    experienceLevel: 'BEGINNER',
    strengths: ['Plot structure', 'Character development'],
    createdAt: '2024-03-22T10:00:00Z',
    updatedAt: '2024-03-22T10:00:00Z',
  },
];

export const mockQueries: MockQuery[] = [
  {
    id: 'query-1',
    writerId: 'writer-2',
    agentId: 'agent-2',
    manuscriptTitle: 'The Last Witness',
    genre: 'Mystery',
    wordCount: 78000,
    queryLetter: 'Dear Mr. Brooks,\n\nI am seeking representation for THE LAST WITNESS, a 78,000-word mystery novel that will appeal to fans of Tana French and Laura Lippman.\n\nDetective Sarah Kane has built her career on solving cold cases, but when a witness to a twenty-year-old murder finally comes forward, Sarah discovers the case is more personal than she ever imagined. The victim was her own mother\'s best friend, and the evidence points to someone close to home.\n\nAs Sarah digs deeper, she uncovers a web of lies that stretches back decades. Every answer leads to more questions, and someone is willing to kill to keep the past buried. Sarah must confront her own family\'s secrets before the killer strikes again.\n\nI am a former police detective with fifteen years of experience in criminal investigations. This is my first novel.\n\nThank you for your consideration.\n\nBest regards,\nJames Chen',
    excerpt: 'The woman in the photo looked familiar, but Sarah couldn\'t place her...',
    status: 'REQUESTED',
    agentResponse: 'Thank you for your query. I\'m intrigued by the premise and your background. Please send the full manuscript for my review.',
    createdAt: '2024-04-01T10:00:00Z',
    updatedAt: '2024-04-10T10:00:00Z',
  },
  {
    id: 'query-2',
    writerId: 'writer-1',
    agentId: 'agent-1',
    manuscriptTitle: 'The Crystal Throne',
    genre: 'Fantasy',
    wordCount: 95000,
    queryLetter: 'Dear Ms. Walsh,\n\nI am seeking representation for THE CRYSTAL THRONE, a 95,000-word YA fantasy that combines the heist elements of SIX OF CROWS with the magic system of BRANDON SANDERSON\'s MISTBORN series.\n\nSeventeen-year-old Kira has always been a thief, but she\'s never stolen anything as dangerous as the Crystal of Eternity. When her sister contracts the Fading Sickness, Kira has no choice—the crystal is the only cure. But the moment she touches it, the crystal bonds to her soul, and Kira discovers she\'s inherited a power that kingdoms have gone to war over.\n\nNow hunted by the ruthless Crystal Guard and sought by the mysterious Shadow Court, Kira must master her newfound abilities before they consume her. With her sister\'s life hanging in the balance and enemies closing in from all sides, Kira faces an impossible choice: surrender the crystal and watch her sister die, or embrace her power and risk becoming the very monster she fears.\n\nI am a librarian and lifelong fantasy reader. This is my debut novel, with series potential.\n\nThank you for your time and consideration.\n\nBest regards,\nSarah Thompson',
    status: 'UNDER_REVIEW',
    createdAt: '2024-04-15T10:00:00Z',
    updatedAt: '2024-04-15T10:00:00Z',
  },
  {
    id: 'query-3',
    writerId: 'writer-3',
    agentId: 'agent-3',
    manuscriptTitle: 'Love in the Time of Coffee',
    genre: 'Romance',
    wordCount: 65000,
    queryLetter: 'Dear Ms. Anderson,\n\nI am seeking representation for LOVE IN THE TIME OF COFFEE, a 65,000-word contemporary romance that will appeal to readers of Emily Henry and Christina Lauren.\n\nMia Garcia never wanted to run a coffee shop. But when her beloved grandmother passes away and leaves her the family café, Mia can\'t bring herself to sell—even when offered a generous buyout by Harrison Cole, the infuriatingly handsome businessman who owns the building.\n\nHarrison needs that space for his corporate expansion, but he finds himself drawn to the passionate barista who won\'t budge. As he gets to know Mia and experiences the community her grandmother built, Harrison begins to question what success really means.\n\nBetween coffee dates and business negotiations, Mia and Harrison discover that sometimes the best things in life aren\'t planned—they\'re brewed one cup at a time.\n\nI work as a barista and have written three romance novels. This is my first to reach the querying stage.\n\nThank you for your consideration.\n\nWarmly,\nEmma Rodriguez',
    status: 'SUBMITTED',
    createdAt: '2024-10-25T10:00:00Z',
    updatedAt: '2024-10-25T10:00:00Z',
  },
];

export const mockFeedback: MockFeedback[] = [
  {
    id: 'feedback-1',
    manuscriptId: 'ms-1',
    readerId: 'reader-1',
    overallImpression: 'This is a strong debut with a unique magic system and compelling protagonist. The plot moves at a good pace and the stakes escalate nicely throughout.',
    strengths: 'The magic system is well-thought-out and original. Kira is a likeable, flawed protagonist with clear motivations. The heist elements add great tension. World-building is immersive without being overwhelming.',
    weaknesses: 'The romance subplot feels rushed in the middle section. Some secondary characters could be more developed, particularly the antagonist. A few pacing issues in chapters 15-18 where the action slows down.',
    suggestions: 'Consider developing the antagonist\'s motivations more clearly. The romance would benefit from more buildup in the early chapters. The middle section could be tightened by combining some of the slower chapters.',
    rating: 4,
    createdAt: '2024-04-15T10:00:00Z',
  },
  {
    id: 'feedback-2',
    manuscriptId: 'ms-1',
    readerId: 'reader-3',
    overallImpression: 'I absolutely loved this! Stayed up until 3am to finish it. The characters feel real and the emotional beats really landed for me.',
    strengths: 'The sister relationship is beautifully written and emotionally resonant. Character voice is strong throughout. The romance develops naturally and the chemistry is excellent. Stakes feel personal and important.',
    weaknesses: 'A few minor continuity errors (Kira\'s eye color changes from blue to green). Some of the fight scenes were hard to follow. The ending feels slightly rushed.',
    suggestions: 'Take time with the ending—it deserves more space to breathe. Consider adding a chapter or two to let the final confrontation and resolution develop fully. Double-check physical descriptions for consistency.',
    rating: 5,
    createdAt: '2024-04-16T10:00:00Z',
  },
];

// Helper function to get user by email
export const getUserByEmail = (email: string): MockUser | undefined => {
  return mockUsers.find(user => user.email === email);
};

// Helper function to get manuscripts by writer
export const getManuscriptsByWriter = (writerId: string): MockManuscript[] => {
  return mockManuscripts.filter(ms => ms.writerId === writerId);
};

// Helper function to get applications by reader
export const getApplicationsByReader = (readerId: string): MockBetaApplication[] => {
  return mockBetaApplications.filter(app => app.readerId === readerId);
};

// Helper function to get applications for a manuscript
export const getApplicationsByManuscript = (manuscriptId: string): MockBetaApplication[] => {
  return mockBetaApplications.filter(app => app.manuscriptId === manuscriptId);
};

// Helper function to get queries by writer
export const getQueriesByWriter = (writerId: string): MockQuery[] => {
  return mockQueries.filter(query => query.writerId === writerId);
};

// Helper function to get queries by agent
export const getQueriesByAgent = (agentId: string): MockQuery[] => {
  return mockQueries.filter(query => query.agentId === agentId);
};

// Helper function to get feedback by manuscript
export const getFeedbackByManuscript = (manuscriptId: string): MockFeedback[] => {
  return mockFeedback.filter(fb => fb.manuscriptId === manuscriptId);
};

// Helper function to get approved readers for a manuscript
export const getApprovedReaders = (manuscriptId: string): MockUser[] => {
  const approvedApps = mockBetaApplications.filter(
    app => app.manuscriptId === manuscriptId && app.status === 'ACCEPTED'
  );
  return approvedApps
    .map(app => mockUsers.find(user => user.id === app.readerId))
    .filter(user => user !== undefined) as MockUser[];
};
